import StickyCard from "../../Moleculas/StickyCard/StickyCard";
import ProfessionalCareer from "./ProfessionalCareer";
import Profile from "./Profile";
import Offices from "./Offices";
import FirmContacts from "./FirmContacts";
import SocialMedia from "./SocialMedia";
import Education from "./Education";
import Languages from "./Languages";
import AOP from "./AOP";
import User from "./User";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useLoaderData,
  type LoaderFunctionArgs,
} from "react-router-dom";
import type { IPersonView } from "../../../interfaces/models/IPersonView";
import { getOfficeByFirm } from "../../../Services/Office";
import {
  deletePerson,
  getPersonByIdOrLegacyId,
} from "../../../Services/Person";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import type { IAreaOfPractice } from "../../../interfaces/models/IAreaOfPractice";
import Bio from "./Bio";
import {
  PersonFormProvider,
  usePersonForm,
  useRegisterFirmContactSync,
  type FirmSourceMode,
} from "./personForm/PersonFormContext";
import { getDirtyPersonFieldLabels } from "./personForm/personFormFieldLabels";
import FormActionBar from "../../staff/FormActionBar";
import { getLanguages } from "../../../Services/languge";
import { ILanguage } from "../../../interfaces";
import { toastPromise } from "../../../utils/toast";
import { getAOPsByFirm } from "../../../Services/AreaOfPractice";
import AuthContext from "../../../store/AuthContext";
import { AccessTypeWithStaff } from "../../../Enums";
import { MemberType } from "../../../Enums/PersonEnums";
import { normalizeRoleToKey } from "../../../Enums/UserEnums";
import { resolveWsgPermission } from "../../../pages/HelpCenter/helpCenterPermissions";
import { getProfessionalsRouteContext } from "../../../pages/ManageFirm/Professionals/useProfessionalsRoutePaths";
import {
  isWsgStaffProfilesFirm,
  shouldShowAreasOfPractice,
  STAFF_PROFILES_LABEL,
} from "../../../pages/ManageFirm/wsgStaffProfiles";
import { useDynamicPageMeta } from "../../../hooks/useDynamicPageMeta";
import { useSelector } from "react-redux";
import type { Appstore } from "../../../redux";
import { getFirmContactByFirmId } from "../../../Services/FirmContact";
import type { FirmContactView } from "../../../interfaces/models/IFirmContact";
import { getJurisdictionLabel } from "../../../utils/jurisdiction/structuredJurisdiction";
import {
  createEmptyFirmContactAssignments,
  type FirmContactAssignmentsMap,
} from "./personForm/firmContactAssignments";

export type AddProfessionalLocationState = {
  person?: IPersonView;
};

/**
 * En el enlace desde la tabla se añade `?skipPersonLoader=1` para que el loader no haga GET
 * (los datos van en `location.state`). Acceso directo / recarga sin ese param: el loader sí busca la persona.
 */
export const SKIP_PERSON_LOADER_SEARCH_PARAM = "skipPersonLoader";

export type AddProfessionalEditLoaderData = {
  person: IPersonView | null;
};

export async function addProfessionalEditLoader({
  request,
  params,
}: LoaderFunctionArgs): Promise<AddProfessionalEditLoaderData> {
  let searchParams: URLSearchParams;
  try {
    searchParams = new URL(request.url).searchParams;
  } catch {
    searchParams = new URL(request.url, "http://localhost").searchParams;
  }
  if (searchParams.get(SKIP_PERSON_LOADER_SEARCH_PARAM) === "1") {
    return { person: null };
  }
  const generatedId = params.generatedId;
  if (!generatedId) {
    throw new Response("Missing person id", { status: 400 });
  }
  const person = await getPersonByIdOrLegacyId(generatedId);
  if (!person) {
    throw new Response("Person not found", { status: 404 });
  }
  return { person: person as unknown as IPersonView };
}

interface AddPersonProps {
  mode: "add" | "edit";
  firmSource?: FirmSourceMode;
  initialPerson?: IPersonView;
  catalogMode?: boolean;
}

const AddProfessionalShell = memo(function AddProfessionalShell({
  mode,
  firmSource = "redux",
  initialPerson: initialPersonProp,
  catalogMode = false,
}: AddPersonProps) {
  const location = useLocation();
  const { generatedId } = useParams<{ generatedId?: string }>();
  const fromNav = (location.state as AddProfessionalLocationState | null)
    ?.person;
  const loaderRaw = useLoaderData() as
    | AddProfessionalEditLoaderData
    | undefined;
  const fromLoader =
    mode === "edit" && generatedId && loaderRaw?.person != null
      ? loaderRaw.person
      : undefined;
  const initialPerson =
    mode === "edit" ? (initialPersonProp ?? fromNav ?? fromLoader) : undefined;

  /** Solo si no hay persona (state ni loader con datos): p. ej. recarga con `skipPersonLoader=1` sin state. */
  const needsRemotePerson =
    mode === "edit" && !initialPerson && Boolean(generatedId);

  const editPersonDocumentId = mode === "edit" ? initialPerson?.id : undefined;

  return (
    <PersonFormProvider
      mode={mode}
      firmSource={firmSource}
      initialPerson={initialPerson}
      requireMainOffice
      requireProfessionalProfileFields
    >
      <AddProfessionalContent
        mode={mode}
        editPersonDocumentId={editPersonDocumentId}
        needsRemotePerson={needsRemotePerson}
        remotePersonKey={generatedId}
        catalogMode={catalogMode}
      />
    </PersonFormProvider>
  );
});

const AddProfessionalContent = memo(function AddProfessionalContent({
  mode,
  editPersonDocumentId,
  needsRemotePerson,
  remotePersonKey,
  catalogMode = false,
}: {
  mode: "add" | "edit";
  editPersonDocumentId?: string;
  needsRemotePerson?: boolean;
  remotePersonKey?: string;
  catalogMode?: boolean;
}) {
  const {
    form,
    submit,
    submitting,
    isDirty,
    state,
    initializeFromPerson,
    firmContactValidationVisible,
  } = usePersonForm();
  const [remoteDocumentId, setRemoteDocumentId] = useState<
    string | undefined
  >();
  const [offices, setOffices] = useState<IOfficeNew[]>([]);
  const [firmContactData, setFirmContactData] = useState<FirmContactView[]>([]);
  const [firmContactAssignments, setFirmContactAssignments] =
    useState<FirmContactAssignmentsMap>(createEmptyFirmContactAssignments);
  const [firmAreasOfPractice, setFirmAreasOfPractice] = useState<
    IAreaOfPractice[]
  >([]);
  const [languages, setLanguages] = useState<ILanguage[]>([]);
  const navigate = useNavigate();
  const { id: routeFirmId } = useParams<{ id?: string }>();
  const location = useLocation();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const { tokenDecoded, loggedPerson } = useContext(AuthContext);
  const manageFirm = useSelector((state: Appstore) => state.manageFirm);

  useEffect(() => {
    if (!isWsgStaffProfilesFirm(routeFirmId) || !routeFirmId) {
      return;
    }
    const profilesPath = `/managefirm/professionals/${routeFirmId}`.toLowerCase();
    const isCreate = /\/create$/i.test(location.pathname);
    setPageMeta({
      title: isCreate ? "Create" : STAFF_PROFILES_LABEL,
      breadcrumbLabels: {
        "/managefirm": "Manage Firm",
        [profilesPath]: STAFF_PROFILES_LABEL,
      },
    });
    return () => clearPageMeta();
  }, [clearPageMeta, location.pathname, routeFirmId, setPageMeta]);

  const selectedFirmMembershipType =
    manageFirm.selectedFirm?.id === form.firm
      ? manageFirm.selectedFirm?.membershipType
      : undefined;
  const selectedFirmName =
    manageFirm.selectedFirm?.id === form.firm
      ? (manageFirm.selectedFirm?.name ??
        manageFirm.selectedFirm?.firmName ??
        "")
      : (offices[0]?.firmName ?? "");

  const dirtyLabels = useMemo(
    () => getDirtyPersonFieldLabels(state.dirtyFields),
    [state.dirtyFields],
  );

  const effectiveEditDocumentId = editPersonDocumentId ?? remoteDocumentId;

  const canDeleteProfessional = useMemo(() => {
    if (getProfessionalsRouteContext(location.pathname) === "dataAdmin") {
      return true;
    }

    const permission = resolveWsgPermission(
      tokenDecoded?.extension_WSG_Permissions,
    );
    if (
      permission === AccessTypeWithStaff.dataAdmin ||
      permission === AccessTypeWithStaff.wsgStaff ||
      permission === AccessTypeWithStaff.wsgSuper
    ) {
      return true;
    }

    const roleKey = normalizeRoleToKey(permission ?? loggedPerson?.role);
    return (
      roleKey === "dataAdmin" ||
      roleKey === "wsgStaff" ||
      roleKey === "wsgSuper"
    );
  }, [
    location.pathname,
    loggedPerson?.role,
    tokenDecoded?.extension_WSG_Permissions,
  ]);

  useEffect(() => {
    if (!needsRemotePerson || !remotePersonKey) {
      setRemoteDocumentId(undefined);
      return;
    }
    setRemoteDocumentId(undefined);
    let cancelled = false;
    void getPersonByIdOrLegacyId(remotePersonKey).then((person) => {
      if (cancelled || !person?.id) {
        return;
      }
      initializeFromPerson(person as unknown as IPersonView);
      setRemoteDocumentId(person.id);
    });
    return () => {
      cancelled = true;
    };
  }, [needsRemotePerson, remotePersonKey, initializeFromPerson]);

  const handleDelete = useCallback(async () => {
    if (!effectiveEditDocumentId) {
      return;
    }
    try {
      await toastPromise(deletePerson(effectiveEditDocumentId), {
        pending: "Deleting person...",
        success: "Person deleted successfully.",
      });
      navigate(-1);
    } catch {
      /* error toast from toastPromise */
    }
  }, [effectiveEditDocumentId, navigate]);

  const handleSave = useCallback(async () => {
    try {
      const saved = await submit();
      if (saved) {
        navigate(-1);
      }
    } catch {
      /* error toast from toastPromise */
    }
  }, [submit, navigate]);

  useEffect(() => {
    if (!form.firm) {
      setOffices([]);
      setFirmAreasOfPractice([]);
      setFirmContactData([]);
      setFirmContactAssignments(createEmptyFirmContactAssignments());
      return;
    }
    let cancelled = false;
    void Promise.all([
      getOfficeByFirm(form.firm),
      getAOPsByFirm(form.firm),
      getFirmContactByFirmId(form.firm),
    ]).then(([officesData, aopsData, firmContactsData]) => {
      if (cancelled) {
        return;
      }
      const o = officesData ?? [];
      const a = Array.isArray(aopsData) ? aopsData : [];
      setOffices(
        o.sort((x, y) =>
          getJurisdictionLabel(x).localeCompare(getJurisdictionLabel(y)),
        ),
      );
      setFirmAreasOfPractice(
        a.sort((x, y) => x.displayName.localeCompare(y.displayName)),
      );
      setFirmContactData(
        Array.isArray(firmContactsData) ? firmContactsData : [],
      );
      setFirmContactAssignments(createEmptyFirmContactAssignments());
    });
    return () => {
      cancelled = true;
    };
  }, [form.firm]);

  const firmContactSyncApi = useMemo(
    () =>
      form.firm
        ? {
            assignments: firmContactAssignments,
            offices,
            firmContactData,
            membershipType: selectedFirmMembershipType,
            firmName: selectedFirmName,
            editPersonId: effectiveEditDocumentId,
          }
        : null,
    [
      firmContactAssignments,
      offices,
      firmContactData,
      selectedFirmMembershipType,
      selectedFirmName,
      effectiveEditDocumentId,
      form.firm,
    ],
  );

  useRegisterFirmContactSync(firmContactSyncApi);

  useEffect(() => {
    let cancelled = false;
    void getLanguages().then((l) => {
      if (!cancelled) {
        setLanguages(l);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <form className="flex w-full flex-col items-start justify-start gap-4 overflow-none text-start">
      <Profile
        offices={offices}
        catalogMode={catalogMode}
        firmIdForTitleOptions={routeFirmId}
        mainOfficeRequired
      />
      <StickyCard title="Office Location" titleClassName="!text-xl">
        <Offices offices={offices} />
      </StickyCard>
      <StickyCard title="Social Media" titleClassName="!text-xl">
        <SocialMedia />
      </StickyCard>
      <StickyCard title="Professional Bio" titleClassName="!text-xl">
        <Bio bioRequired={form.memberType === MemberType.member} />
      </StickyCard>
      <StickyCard title="Education Information" titleClassName="!text-xl">
        <Education />
      </StickyCard>
      <StickyCard title="Languages" titleClassName="!text-xl">
        <Languages
          languages={languages}
          onLanguageCreated={(lang) => {
            setLanguages((prev) => {
              const without = prev.filter((l) => l.name !== lang.name);
              return [...without, lang].sort((a, b) =>
                a.name.localeCompare(b.name),
              );
            });
          }}
        />
      </StickyCard>
      {shouldShowAreasOfPractice({
        firmId: form.firm,
        routeFirmId,
        parentFirmId: manageFirm.selectedFirm?.parentFirm,
        isAttorney: form.isAttorney,
        memberType: form.memberType,
      }) && (
        <div id="person-form-areas-of-practice" className="w-full">
          <StickyCard title="Areas of Practice" titleClassName="!text-xl">
            <AOP firmAreasOfPractice={firmAreasOfPractice} />
          </StickyCard>
        </div>
      )}
      <StickyCard title="Professional Career" titleClassName="!text-xl">
        <ProfessionalCareer />
      </StickyCard>
      <StickyCard title="User Information" titleClassName="!text-xl">
        <User />
      </StickyCard>
      <FormActionBar
        mode={mode}
        isDirty={isDirty}
        dirtyFieldLabels={dirtyLabels}
        onSave={() => void handleSave()}
        onDelete={
          mode === "edit" && effectiveEditDocumentId ? handleDelete : undefined
        }
        disabled={submitting}
        deleteConfirmMessage="Are you sure you want to delete this professional?"
        showDeleteButton={canDeleteProfessional}
      />
    </form>
  );
});

const AddProfessional: React.FC<AddPersonProps> = (props) => (
  <AddProfessionalShell {...props} />
);

export default AddProfessional;
